import { useState, useCallback, useMemo } from 'react';

/**
 * Generic filter state management hook
 *
 * @param {Object} initialFilters - Initial filter state
 * @returns {Object} Filter state and management functions
 *
 * @example
 * const { filters, addFilter, removeFilter, clearFilters } = useFilters({
 *   state: [],
 *   designation: [],
 * });
 *
 * // Add a filter value
 * addFilter('state', 'CA');
 *
 * // Remove a filter value
 * removeFilter('state', 'CA');
 *
 * // Check if a filter is active
 * hasFilter('state', 'CA'); // true/false
 *
 * // Clear all filters
 * clearFilters();
 */
export function useFilters(initialFilters = {}) {
  const [filters, setFilters] = useState(initialFilters);

  /**
   * Add a value to a filter key (supports multi-select)
   */
  const addFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const currentValues = prev[key] || [];
      // Avoid duplicates
      if (currentValues.includes(value)) {
        return prev;
      }
      return {
        ...prev,
        [key]: [...currentValues, value],
      };
    });
  }, []);

  /**
   * Remove a value from a filter key
   */
  const removeFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const currentValues = prev[key] || [];
      const newValues = currentValues.filter((v) => v !== value);
      return {
        ...prev,
        [key]: newValues,
      };
    });
  }, []);

  /**
   * Toggle a filter value (add if not present, remove if present)
   */
  const toggleFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const currentValues = prev[key] || [];
      if (currentValues.includes(value)) {
        return {
          ...prev,
          [key]: currentValues.filter((v) => v !== value),
        };
      }
      return {
        ...prev,
        [key]: [...currentValues, value],
      };
    });
  }, []);

  /**
   * Set a filter to a single value (replaces existing values)
   */
  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: Array.isArray(value) ? value : [value],
    }));
  }, []);

  /**
   * Clear a specific filter key
   */
  const clearFilter = useCallback((key) => {
    setFilters((prev) => ({
      ...prev,
      [key]: [],
    }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    const cleared = Object.keys(filters).reduce((acc, key) => {
      acc[key] = [];
      return acc;
    }, {});
    setFilters(cleared);
  }, [filters]);

  /**
   * Check if a specific filter value is active
   */
  const hasFilter = useCallback(
    (key, value) => {
      const values = filters[key] || [];
      return values.includes(value);
    },
    [filters]
  );

  /**
   * Get all values for a filter key
   */
  const getFilterValues = useCallback(
    (key) => {
      return filters[key] || [];
    },
    [filters]
  );

  /**
   * Get count of active filters
   */
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).reduce((count, values) => {
      return count + (values?.length || 0);
    }, 0);
  }, [filters]);

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = useMemo(() => {
    return activeFilterCount > 0;
  }, [activeFilterCount]);

  /**
   * Get all active filters as flat array of { key, value } objects
   */
  const activeFiltersArray = useMemo(() => {
    const result = [];
    Object.entries(filters).forEach(([key, values]) => {
      if (values && values.length > 0) {
        values.forEach((value) => {
          result.push({ key, value });
        });
      }
    });
    return result;
  }, [filters]);

  return {
    filters,
    setFilters,
    addFilter,
    removeFilter,
    toggleFilter,
    setFilter,
    clearFilter,
    clearFilters,
    hasFilter,
    getFilterValues,
    activeFilterCount,
    hasActiveFilters,
    activeFiltersArray,
  };
}

export default useFilters;
